/**
 * Water surfaces at runtime: one entity per water plane under the scene-content root, drawn with
 * the v2 water shader (waterShaders.ts) at the quality tier the device gets (waterParams.ts).
 *
 * Render order and what each tier needs:
 * - 'high' / 'low' water lives in WaterLayer, a transparent sub-layer right after the splats
 *   (World transparent), so it can see them. It fades against the splats' depth
 *   (SplatDepthPass, requested through `needsSplatDepth`), which both hides it behind splats in
 *   front and measures how deep the water is at every pixel.
 * - While a model (an opaque custom mesh) reaches below a plane on screen, the water also takes
 *   the main camera's scene depth map, which holds the models (splats write no depth): the
 *   submerged part of a model is then the bottom there, seen through the water between the
 *   surface and the model, as the splats are. Scenes without a model in their water pay nothing.
 * - 'high' also needs a copy of the frame after the splats: the main camera's scene colour map.
 *   The engine grabs it at the Depth layer, so the first time it is needed the Depth layer is
 *   moved to just after World transparent (nothing else in the viewer requests the colour or
 *   depth map). With post-processing on (CameraFrame) the same grab point is set on the
 *   CameraFrame instead. Requested only while a high-tier plane is on screen.
 * - 'high' planes with planar reflections share reflection cameras like the mirrors' (reflected
 *   through the water plane, oblique near plane, gsplat clip plane), at half resolution: one per
 *   distinct water level (two pools at the same height share one), at most
 *   MAX_REFLECTION_PLANES, turned off while no plane on that level is in view; planes beyond the
 *   cap reflect the sky.
 * - 'opaque' water (XR) sits in the mirror layer before the splats and writes depth, like the
 *   original water: occluded by draw order, no see-through.
 *
 * Animation: each plane keeps its own wave clock (speed changes never jump), wrapped every
 * TIME_PERIOD seconds where every scroll speed and swell frequency repeats exactly. Planes
 * report whether they are animating on screen so on-demand rendering keeps drawing only then.
 */
import * as pc from 'playcanvas';
import type { WaterPlaneData } from '../../types';
import { buildPolygonMesh, HORIZONTAL_LAYOUT, polygonBounds, sanitizeFootprint, type Polygon2D } from '../../utils/polygonMesh';
import {
    hexToLinear,
    resolveWaterLook,
    selectWaterQuality,
    sunDirection,
    usesPlanarReflection,
    usesRefractionGrab,
    waterCoverageTolerance,
    waterShapeKind,
    waterShoreDistance,
    waterVisibilityDistance,
    type WaterLook,
    type WaterQualityInputs,
    type WaterQualityTier,
} from './waterParams';
import { generateWaterNormalMap } from './waterNormalMap';
import { patchGpuSortClip, setSplatClipPlane } from '../../dynamic-viewer/gsplatClipPlane';
import type { WaterSplitPlane } from './waterSplatSplit';
import { WATER_CLOUD_SHADOW_DEFINE, WATER_DEFINES, WATER_FS_GLSL, WATER_FS_WGSL, WATER_VS_GLSL, WATER_VS_WGSL } from './waterShaders';

/** Seconds after which every wave scroll and swell phase repeats exactly. */
export const WATER_TIME_PERIOD = 1000;
/** Reflection cameras stay on this many frames after their plane leaves the view (no churn at screen edges). */
const REFLECTION_DISABLE_AFTER_FRAMES = 45;
/** Unused reflection cameras (and their render targets) are freed after this many frames. */
const REFLECTION_RELEASE_AFTER_FRAMES = 600;
/** Each reflection is a second render of the splats: at most this many water levels get one. */
export const MAX_REFLECTION_PLANES = 2;
const ELLIPSE_SEGMENTS = 48;
/** The unit plane primitive spans -0.5..0.5; the ellipse matches it. */
const UNIT_RECT: Polygon2D = [
    { u: -0.5, v: -0.5 },
    { u: 0.5, v: -0.5 },
    { u: 0.5, v: 0.5 },
    { u: -0.5, v: 0.5 },
];

interface Vec3Like {
    x: number;
    y: number;
    z: number;
}

type ProjectionCallback = (projMatrix: pc.Mat4, view: number) => void;

export interface WaterSystemDeps {
    app: pc.AppBase;
    /** Main camera entity. */
    camera: pc.Entity;
    /** Scene-content root the planes are children of (AR placement moves / scales it). */
    sceneContent: pc.Entity;
    /** Layer for 'opaque' tier water (the mirror layer: before the splats, writes depth). */
    opaqueLayer: pc.Layer;
    /** Translucent entities' layer: WaterLayer's transparent sub-layer goes right before it. */
    translucentLayer: pc.Layer;
    /** Mobile / performance mode / XR, read every frame. */
    qualityInputs: () => WaterQualityInputs;
    /** Post-processing CameraFrame when active (its own colour grab replaces the camera's). */
    cameraFrame: () => pc.CameraFrame | null;
    /** Install the gsplat clip-plane chunk and the main camera's clip clear (shared with mirrors). */
    ensureSplatClip: () => void;
    /** Ask on-demand rendering for a frame (a parameter changed). */
    requestFrame: (reason: string) => void;
    /** World bounds of the opaque models (custom meshes), read every frame a depth-tier plane is on screen. */
    modelBounds?: () => Iterable<pc.BoundingBox>;
    /** Scene-space vector (editor coords, BabylonJS-era) → PlayCanvas local position (z negated). */
    toPlayCanvasPosition: (value: unknown) => Vec3Like;
    /** Finite vector from a stored value ({x,y,z} or {_x,_y,_z}). */
    finiteVec3: (value: unknown, fallback?: Vec3Like) => Vec3Like;
}

/** One reflection camera, shared by every water plane on the same level (plane). */
interface WaterReflection {
    key: string;
    entity: pc.Entity;
    /** The reflecting plane (world): normal and distance, N.P = d. */
    normal: pc.Vec3;
    d: number;
    /** This camera's splat clip plane (normal, d): the water plane. */
    clip: Float32Array;
    /** The reflected camera position (for the oblique near plane). */
    reflPos: pc.Vec3;
    /** Last look-at target and up written, so a still camera writes no transform (on-demand idling). */
    lastTarget: pc.Vec3;
    lastUp: pc.Vec3;
    lastPos: pc.Vec3;
    usedThisFrame: boolean;
    idleFrames: number;
    target: pc.RenderTarget;
    texture: pc.Texture;
    outOfViewFrames: number;
}

interface WaterPlaneRuntime {
    id: string;
    data: WaterPlaneData;
    look: WaterLook;
    /** Keyframe-animated overrides on top of `look` (water.color etc.), cleared by resetAnimated. */
    animated: Partial<Pick<WaterLook, 'color' | 'deepColor' | 'waveStrength' | 'waveSpeed' | 'waveScale' | 'reflectivity'>>;
    entity: pc.Entity;
    meshChild: pc.Entity;
    /** Custom polygon mesh (destroyed with the plane); null for the plane primitive or no shape. */
    mesh: pc.Mesh | null;
    material: pc.ShaderMaterial;
    /** Diagonal of the footprint in the plane's local units (times its scale = size in scene units). */
    localExtent: number;
    time: number;
    tier: WaterQualityTier | null;
    planar: boolean;
    visible: boolean;
    /** The reflection texture bound last (avoids re-setting the parameter every frame). */
    boundReflection: pc.Texture | null;
}

const lookOf = (plane: WaterPlaneRuntime): WaterLook => ({ ...plane.look, ...plane.animated });

/**
 * Whether a model stands in a (horizontal) water plane: its bounds overlap the plane's outline
 * bounds and reach below the surface. Conservative for a tilted plane (more yes, never a wrong no
 * for a level one).
 */
export function modelInWater(planeBox: pc.BoundingBox, model: pc.BoundingBox): boolean {
    const pMin = planeBox.getMin();
    const pMax = planeBox.getMax();
    const mMin = model.getMin();
    const mMax = model.getMax();
    // inside the outline's bounds, and some of it below the surface
    return mMax.x > pMin.x && mMin.x < pMax.x && mMax.z > pMin.z && mMin.z < pMax.z && mMin.y < pMax.y;
}

const _frustumPlane = new pc.Plane();
/**
 * Whether a box overlaps the view frustum, grown by `margin` world units (the frustum is last
 * frame's, and a plane coming into view needs its inputs ready). Conservative: may say yes for a
 * box just outside a frustum corner, never no for a visible one.
 */
export function aabbInFrustum(aabb: pc.BoundingBox, frustum: pc.Frustum, margin = 0): boolean {
    const c = aabb.center;
    const h = aabb.halfExtents;
    for (let p = 0; p < 6; p++) {
        // Frustum#planes is gone since PlayCanvas 2.22 (it returns []): read each plane instead
        const plane = frustum.getPlane(p, _frustumPlane);
        const n = plane.normal;
        // distance of the box's most positive corner along the plane normal
        const reach = Math.abs(n.x) * h.x + Math.abs(n.y) * h.y + Math.abs(n.z) * h.z;
        if (n.x * c.x + n.y * c.y + n.z * c.z + plane.distance + reach + margin < 0) return false;
    }
    return true;
}

/** Local footprint of a plane's shape, or null when it has none to draw (an outline not drawn yet). */
export function waterFootprint(data: Pick<WaterPlaneData, 'shape' | 'footprint'>): Polygon2D | null {
    const kind = waterShapeKind(data);
    if (kind === 'rectangle') return UNIT_RECT;
    if (kind === 'ellipse') {
        const points: Polygon2D = [];
        for (let i = 0; i < ELLIPSE_SEGMENTS; i++) {
            const a = (i / ELLIPSE_SEGMENTS) * Math.PI * 2;
            points.push({ u: Math.cos(a) * 0.5, v: Math.sin(a) * 0.5 });
        }
        return points;
    }
    return sanitizeFootprint(data.footprint);
}

const srgbToLinear = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const toHex = (rgb: ArrayLike<number>) =>
    '#' + [0, 1, 2].map((i) => Math.round(clamp01(rgb[i]) * 255).toString(16).padStart(2, '0')).join('');
const hexToSrgb = (hex: string): [number, number, number] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as [number, number, number];

export class WaterSystem {
    readonly layer: pc.Layer;
    private readonly deps: WaterSystemDeps;
    private readonly planes = new Map<string, WaterPlaneRuntime>();
    /** Clouds cast shadows: the sun's glint dims under them (one shader variant, only then). */
    private cloudShadows = false;
    private detailTexture: pc.Texture | null = null;
    /** 1x1 transparent black: the reflection before its camera exists, the frame copy while off. */
    private placeholder: pc.Texture | null = null;
    private detailSize = 0;
    private tier: WaterQualityTier;
    private grabRequested = false;
    /** The main camera's scene depth map is requested (a model stands in water on screen). */
    private depthMapRequested = false;
    private cameraFrameDepth: pc.CameraFrame | null = null;
    private depthLayerMoved = false;
    private cameraFrameGrab: pc.CameraFrame | null = null;
    private gpuSortClipPatched = false;
    private needDepth = false;
    private animatingOnScreen = false;
    private sunScanFrame = 0;
    private sceneSun: { dir: pc.Vec3; color: [number, number, number] } | null = null;
    private lastUpdate = -1;
    private readonly onUpdate: (dt: number) => void;
    private readonly onPrerender: () => void;
    // scratch
    private readonly n = new pc.Vec3();
    private readonly t = new pc.Vec3();
    private readonly b = new pc.Vec3();

    constructor(deps: WaterSystemDeps) {
        this.deps = deps;
        const { app } = deps;
        this.layer = new pc.Layer({ name: 'WaterLayer', transparentSortMode: pc.SORTMODE_BACK2FRONT });
        const layers = app.scene.layers;
        const translucentIdx = layers.getTransparentIndex(deps.translucentLayer);
        const worldIdx = layers.getTransparentIndex(layers.getLayerById(pc.LAYERID_WORLD)!);
        const at = translucentIdx >= 0 ? translucentIdx : worldIdx >= 0 ? worldIdx + 1 : layers.layerList.length;
        layers.insertTransparent(this.layer, at);
        const cam = deps.camera.camera;
        if (cam && !cam.layers.includes(this.layer.id)) {
            const list = [...cam.layers];
            const translucentCamIdx = list.indexOf(deps.translucentLayer.id);
            list.splice(translucentCamIdx >= 0 ? translucentCamIdx : list.length, 0, this.layer.id);
            cam.layers = list;
        }
        this.tier = selectWaterQuality(deps.qualityInputs());
        this.onUpdate = (dt: number) => this.tick(dt);
        this.onPrerender = () => this.prerender();
        app.on('update', this.onUpdate);
        app.on('prerender', this.onPrerender);
    }

    // ── public API ───────────────────────────────────────────────────────────────────────

    /** Switch the cloud-shadowed sun glint on or off (clouds/cloudMeshShadows.ts binds the uniforms). */
    setCloudShadows(on: boolean): void {
        if (on === this.cloudShadows) return;
        this.cloudShadows = on;
        for (const plane of this.planes.values()) {
            plane.material.setDefine(WATER_CLOUD_SHADOW_DEFINE, on);
            plane.material.update();
        }
        this.deps.requestFrame('water');
    }

    get size(): number {
        return this.planes.size;
    }

    get qualityTier(): WaterQualityTier {
        return this.tier;
    }

    has(id: string): boolean {
        return this.planes.has(id);
    }

    entity(id: string): pc.Entity | null {
        return this.planes.get(id)?.entity ?? null;
    }

    data(id: string): WaterPlaneData | null {
        return this.planes.get(id)?.data ?? null;
    }

    /** The plane an entity belongs to (animation system lookups). */
    planeOfEntity(entity: pc.Entity): string | null {
        for (const plane of this.planes.values()) if (plane.entity === entity) return plane.id;
        return null;
    }

    /** True while the splat depth map is needed this frame (a high / low tier plane on screen). */
    needsSplatDepth(): boolean {
        return this.needDepth;
    }

    /** True while an animated plane is on screen (on-demand rendering keeps drawing). */
    isAnimatingOnScreen(): boolean {
        return this.animatingOnScreen;
    }

    /**
     * The high / low tier planes on screen this frame, for the splat draw's split at the water
     * (waterSplatSplit.ts): world plane, bounds, and the height above the surface from which a
     * splat stands in front of the water (where the shader's coverage test starts to give way).
     */
    splitPlanes(): WaterSplitPlane[] {
        const out: WaterSplitPlane[] = [];
        if (this.tier === 'opaque') return out;
        const unitScale = Math.abs(this.deps.sceneContent.getWorldTransform().getScale().x) || 1;
        for (const plane of this.planes.values()) {
            if (!plane.visible || plane.tier === 'opaque') continue;
            const aabb = plane.meshChild.render?.meshInstances?.[0]?.aabb;
            if (!aabb) continue;
            const normal = plane.entity.up.clone().normalize();
            const look = lookOf(plane);
            out.push({
                normal,
                d: normal.dot(plane.entity.getPosition()),
                splitHeight: waterCoverageTolerance(this.planeExtent(plane), look.shoreSoftness) * unitScale,
                aabb,
            });
        }
        return out;
    }

    /** The plane's size (footprint diagonal) in scene units, as the shader's metrics take it. */
    private planeExtent(plane: WaterPlaneRuntime): number {
        const s = plane.entity.getLocalScale();
        return Math.max(plane.localExtent * Math.hypot(s.x, s.z) / Math.SQRT2, 1e-3);
    }

    add(data: WaterPlaneData, index = this.planes.size): pc.Entity {
        const id = data.id || `water-${Date.now()}-${index}`;
        if (this.planes.has(id)) this.remove(id);
        const entity = new pc.Entity(id);
        const meshChild = new pc.Entity(`${id}-mesh`);
        entity.addChild(meshChild);
        const material = this.createMaterial();
        const plane: WaterPlaneRuntime = {
            id,
            data: { ...data, id },
            look: resolveWaterLook(data),
            animated: {},
            entity,
            meshChild,
            mesh: null,
            material,
            localExtent: Math.SQRT2,
            time: 0,
            tier: null,
            planar: false,
            visible: false,
            boundReflection: null,
        };
        this.planes.set(id, plane);
        this.applyTransform(plane, data);
        this.buildMesh(plane);
        this.applyTier(plane);
        this.deps.sceneContent.addChild(entity);
        this.deps.requestFrame('water');
        return entity;
    }

    /** Apply a partial update: transform in place, a new shape rebuilds the mesh, look fields re-resolve. */
    updatePlane(id: string, patch: Partial<WaterPlaneData>): void {
        const plane = this.planes.get(id);
        if (!plane || !patch) return;
        const next: WaterPlaneData = { ...plane.data, ...patch, id: plane.id };
        plane.data = next;
        if (patch.position || patch.rotation || patch.scale) this.applyTransform(plane, patch);
        if ('footprint' in patch || 'shape' in patch) this.buildMesh(plane);
        plane.look = resolveWaterLook(next);
        this.applyTier(plane);
        this.deps.requestFrame('water');
    }

    remove(id: string): void {
        const plane = this.planes.get(id);
        if (!plane) return;
        this.planes.delete(id);
        plane.material.destroy();
        plane.mesh?.destroy();
        plane.entity.destroy();
        this.deps.requestFrame('water');
    }

    /** Remove every plane (another scene is loading); the system stays ready for the next. */
    clear(): void {
        for (const id of [...this.planes.keys()]) this.remove(id);
    }

    /** Ids of the live planes. */
    ids(): string[] {
        return [...this.planes.keys()];
    }

    destroy(): void {
        this.clear();
        const { app } = this.deps;
        app.off('update', this.onUpdate);
        app.off('prerender', this.onPrerender);
        this.setGrab(false);
        this.setDepthMap(false);
        for (const r of [...this.reflections.values()]) this.releaseReflection(r);
        this.detailTexture?.destroy();
        this.detailTexture = null;
        this.placeholder?.destroy();
        this.placeholder = null;
        app.scene.layers.remove(this.layer);
    }

    // ── animation properties (keyframes: water.color.r etc., colours in sRGB 0..1) ──────────

    readAnimatable(entity: pc.Entity): Record<string, number> | null {
        const id = this.planeOfEntity(entity);
        const plane = id ? this.planes.get(id) : null;
        if (!plane) return null;
        const look = lookOf(plane);
        const c = hexToSrgb(look.color);
        const d = hexToSrgb(look.deepColor);
        return {
            'water.color.r': c[0], 'water.color.g': c[1], 'water.color.b': c[2],
            'water.deepColor.r': d[0], 'water.deepColor.g': d[1], 'water.deepColor.b': d[2],
            'water.waveStrength': look.waveStrength,
            'water.waveSpeed': look.waveSpeed,
            'water.waveScale': look.waveScale,
            'water.reflectivity': look.reflectivity,
        };
    }

    /** Returns true when anything changed. */
    writeAnimatable(entity: pc.Entity, values: Map<string, number>): boolean {
        const id = this.planeOfEntity(entity);
        const plane = id ? this.planes.get(id) : null;
        if (!plane) return false;
        const look = lookOf(plane);
        let touched = false;
        const color = (prefix: string, hex: string): string | null => {
            if (!values.has(`${prefix}.r`) && !values.has(`${prefix}.g`) && !values.has(`${prefix}.b`)) return null;
            const cur = hexToSrgb(hex);
            return toHex([values.get(`${prefix}.r`) ?? cur[0], values.get(`${prefix}.g`) ?? cur[1], values.get(`${prefix}.b`) ?? cur[2]]);
        };
        const c = color('water.color', look.color);
        if (c) { plane.animated.color = c; touched = true; }
        const d = color('water.deepColor', look.deepColor);
        if (d) { plane.animated.deepColor = d; touched = true; }
        if (values.has('water.waveStrength')) { plane.animated.waveStrength = Math.max(0, values.get('water.waveStrength')!); touched = true; }
        if (values.has('water.waveSpeed')) { plane.animated.waveSpeed = Math.max(0, values.get('water.waveSpeed')!); touched = true; }
        if (values.has('water.waveScale')) { plane.animated.waveScale = Math.max(0.05, values.get('water.waveScale')!); touched = true; }
        if (values.has('water.reflectivity')) { plane.animated.reflectivity = clamp01(values.get('water.reflectivity')!); touched = true; }
        return touched;
    }

    resetAnimatable(entity: pc.Entity, properties: string[]): void {
        const id = this.planeOfEntity(entity);
        const plane = id ? this.planes.get(id) : null;
        if (!plane) return;
        if (properties.some((p) => p.startsWith('water.color'))) delete plane.animated.color;
        if (properties.some((p) => p.startsWith('water.deepColor'))) delete plane.animated.deepColor;
        if (properties.includes('water.waveStrength')) delete plane.animated.waveStrength;
        if (properties.includes('water.waveSpeed')) delete plane.animated.waveSpeed;
        if (properties.includes('water.waveScale')) delete plane.animated.waveScale;
        if (properties.includes('water.reflectivity')) delete plane.animated.reflectivity;
    }

    // ── construction ─────────────────────────────────────────────────────────────────────

    private applyTransform(plane: WaterPlaneRuntime, data: Partial<WaterPlaneData>): void {
        const { toPlayCanvasPosition, finiteVec3 } = this.deps;
        if (data.position) {
            const p = toPlayCanvasPosition(data.position);
            plane.entity.setLocalPosition(p.x, p.y, p.z);
        }
        if (data.rotation) {
            // BabylonJS-era Euler radians → PlayCanvas degrees with Z negation (as mirrors).
            const r = finiteVec3(data.rotation);
            plane.entity.setLocalEulerAngles((r.x * 180) / Math.PI, (r.y * 180) / Math.PI, (-r.z * 180) / Math.PI);
        }
        if (data.scale) {
            const s = finiteVec3(data.scale, { x: 2, y: 1, z: 2 });
            plane.entity.setLocalScale(s.x, s.y, s.z);
        }
    }

    private buildMesh(plane: WaterPlaneRuntime): void {
        const { app } = this.deps;
        if (plane.meshChild.render) plane.meshChild.removeComponent('render');
        plane.mesh?.destroy();
        plane.mesh = null;
        const kind = waterShapeKind(plane.data);
        const footprint = waterFootprint(plane.data);
        if (!footprint) {
            plane.localExtent = Math.SQRT2;
            return;
        }
        const bounds = polygonBounds(footprint);
        plane.localExtent = Math.hypot(bounds.maxU - bounds.minU, bounds.maxV - bounds.minV);
        const layerId = this.layerFor(this.tier);
        if (kind === 'rectangle') {
            plane.meshChild.addComponent('render', { type: 'plane', castShadows: false, receiveShadows: false, layers: [layerId] });
        } else {
            const mesh = buildPolygonMesh(app.graphicsDevice, footprint, HORIZONTAL_LAYOUT);
            if (!mesh) return;
            plane.mesh = mesh;
            const mi = new pc.MeshInstance(mesh, plane.material);
            plane.meshChild.addComponent('render', { meshInstances: [mi], castShadows: false, receiveShadows: false, layers: [layerId] });
        }
        plane.meshChild.render?.meshInstances.forEach((mi) => {
            mi.material = plane.material;
        });
        plane.tier = null; // re-apply the layer for the current tier
    }

    private createMaterial(): pc.ShaderMaterial {
        const material = new pc.ShaderMaterial({
            // One program per tier variant, shared by every plane (uniforms are per material).
            uniqueName: 'StorySplatWaterV2',
            vertexGLSL: WATER_VS_GLSL,
            fragmentGLSL: WATER_FS_GLSL,
            vertexWGSL: WATER_VS_WGSL,
            fragmentWGSL: WATER_FS_WGSL,
            attributes: { aPosition: pc.SEMANTIC_POSITION },
        });
        material.cull = pc.CULLFACE_NONE;
        material.depthTest = true;
        if (this.cloudShadows) material.setDefine(WATER_CLOUD_SHADOW_DEFINE, true);
        return material;
    }

    private layerFor(tier: WaterQualityTier): number {
        return tier === 'opaque' ? this.deps.opaqueLayer.id : this.layer.id;
    }

    /** Defines, blending and layer for the current tier and the plane's reflection mode. */
    private applyTier(plane: WaterPlaneRuntime): void {
        const tier = this.tier;
        const planar = usesPlanarReflection(tier, lookOf(plane));
        if (plane.tier === tier && plane.planar === planar) return;
        const m = plane.material;
        for (const define of WATER_DEFINES) m.setDefine(define, false);
        if (tier === 'high') {
            m.setDefine('WATER_HIGH', true);
            m.setDefine('WATER_DEPTH', true);
            if (usesRefractionGrab(tier)) m.setDefine('WATER_REFRACTION', true);
            if (planar) m.setDefine('WATER_PLANAR', true);
        } else if (tier === 'low') {
            m.setDefine('WATER_DEPTH', true);
        } else {
            m.setDefine('WATER_OPAQUE', true);
        }
        if (tier === 'opaque') {
            m.blendType = pc.BLEND_NONE;
            m.depthWrite = true;
        } else {
            m.blendType = pc.BLEND_PREMULTIPLIED;
            m.depthWrite = false;
        }
        if (planar) this.bindReflection(plane, null);
        m.update();
        if (plane.meshChild.render) plane.meshChild.render.layers = [this.layerFor(tier)];
        plane.tier = tier;
        plane.planar = planar;
    }

    private placeholderTexture(): pc.Texture {
        if (!this.placeholder) {
            this.placeholder = new pc.Texture(this.deps.app.graphicsDevice, {
                name: 'StorySplatWaterPlaceholder',
                width: 1,
                height: 1,
                format: pc.PIXELFORMAT_RGBA8,
                mipmaps: false,
                levels: [new Uint8Array([0, 0, 0, 0])],
            });
        }
        return this.placeholder;
    }

    private ensureDetailTexture(): pc.Texture {
        const size = this.tier === 'high' ? 256 : 128;
        if (this.detailTexture && this.detailSize >= size) return this.detailTexture;
        this.detailTexture?.destroy();
        const { pixels } = generateWaterNormalMap(size);
        const texture = new pc.Texture(this.deps.app.graphicsDevice, {
            name: 'StorySplatWaterDetail',
            width: size,
            height: size,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: true,
            minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_REPEAT,
            addressV: pc.ADDRESS_REPEAT,
            anisotropy: 4,
            levels: [pixels],
        });
        this.detailTexture = texture;
        this.detailSize = size;
        return texture;
    }

    // ── per frame ────────────────────────────────────────────────────────────────────────

    private tick(dt: number): void {
        if (!this.planes.size) {
            this.needDepth = false;
            this.animatingOnScreen = false;
            if (this.grabRequested || this.cameraFrameGrab) this.setGrab(false);
            if (this.depthMapRequested || this.cameraFrameDepth) this.setDepthMap(false);
            return;
        }
        const nextTier = selectWaterQuality(this.deps.qualityInputs());
        if (nextTier !== this.tier) {
            this.tier = nextTier;
            this.deps.requestFrame('water-quality');
        }
        const step = Math.min(Math.max(dt, 0), 0.25);
        const cam = this.deps.camera.camera;
        let anyDepth = false;
        let anyGrab = false;
        let animating = false;
        let models: pc.BoundingBox[] | null = null;
        let modelDepth = false;
        for (const plane of this.planes.values()) {
            this.applyTier(plane);
            const look = lookOf(plane);
            if (!look.stillWater) plane.time = (plane.time + step * look.waveSpeed) % WATER_TIME_PERIOD;
            plane.visible = this.isOnScreen(plane, cam);
            if (!plane.visible) continue;
            if (this.tier !== 'opaque') {
                anyDepth = true;
                if (!modelDepth && this.deps.modelBounds) {
                    models ??= [...this.deps.modelBounds()];
                    const box = plane.meshChild.render?.meshInstances?.[0]?.aabb;
                    if (box) modelDepth = models.some((m) => modelInWater(box, m));
                }
            }
            if (usesRefractionGrab(this.tier)) anyGrab = true;
            if (!look.stillWater && look.waveSpeed > 0) animating = true;
        }
        this.needDepth = anyDepth;
        this.animatingOnScreen = animating;
        this.setGrab(anyGrab);
        this.setDepthMap(modelDepth);
        if (++this.sunScanFrame >= 30 || this.lastUpdate < 0) {
            this.sunScanFrame = 0;
            this.scanSun();
        }
        this.lastUpdate = 1;
    }

    /** Uniforms and reflection cameras, after all updates (camera moved) and before culling. */
    private prerender(): void {
        if (!this.planes.size) return;
        const cam = this.deps.camera;
        const camPos = cam.getPosition();
        const camFwd = cam.forward;
        const texture = this.ensureDetailTexture();
        const unitScale = Math.max(this.deps.sceneContent.getWorldTransform().getScale().x, 1e-6);
        const invUnit = 1 / Math.abs(unitScale);
        const fovRad = ((cam.camera?.fov ?? 60) * Math.PI) / 180;
        const pixel = (2 * Math.tan(fovRad / 2)) / Math.max(this.deps.app.graphicsDevice.height, 1);
        const clear = cam.camera?.clearColor ?? new pc.Color(0.5, 0.5, 0.5, 1);
        const cl = [srgbToLinear(clear.r), srgbToLinear(clear.g), srgbToLinear(clear.b)];
        // Sky the water reflects where the planar reflection (or the low tier) has none: the scene's
        // background colour, bluer and a little darker overhead, paler at the horizon, never brighter
        // than a daylight sky and never fully black (water always picks up some sky light).
        const lum = Math.max(0.05, Math.min(1, 0.2126 * cl[0] + 0.7152 * cl[1] + 0.0722 * cl[2]));
        const zenith = [0, 1, 2].map((i) => cl[i] * 0.45 + [0.16, 0.33, 0.68][i] * Math.min(1, lum * 2.2) * 0.55);
        const horizon = [0, 1, 2].map((i) => cl[i] * 0.6 + [0.78, 0.85, 0.92][i] * Math.min(1, lum * 2) * 0.4);
        for (const plane of this.planes.values()) {
            const m = plane.material;
            const look = lookOf(plane);
            const world = plane.entity.getWorldTransform();
            world.getY(this.n).normalize();
            world.getX(this.t).normalize();
            world.getZ(this.b).normalize();
            const extent = this.planeExtent(plane);
            const still = look.stillWater;
            const strength = look.waveStrength * (still ? 0.12 : 1);
            const freq = 0.5 * look.waveScale;
            const dir = (look.waveDirection * Math.PI) / 180;
            const clarity = look.clarity;
            const maxTransmission = clamp01(clarity * 4) * (0.5 + 0.5 * clarity);
            m.setParameter('uTime', plane.time);
            m.setParameter('uCameraPos', [camPos.x, camPos.y, camPos.z]);
            m.setParameter('uCameraForward', [camFwd.x, camFwd.y, camFwd.z]);
            m.setParameter('uPlaneNormal', [this.n.x, this.n.y, this.n.z]);
            m.setParameter('uPlaneTangent', [this.t.x, this.t.y, this.t.z]);
            m.setParameter('uPlaneBitangent', [this.b.x, this.b.y, this.b.z]);
            m.setParameter('uMetrics', [invUnit, extent, waterVisibilityDistance(clarity, extent), waterShoreDistance(extent, look.shoreSoftness)]);
            m.setParameter('uMetrics2', [this.detailSize || 256, maxTransmission, 0.35 * look.waveScale, 0.035 * (0.5 + 0.5 * Math.min(strength, 2))]);
            m.setParameter('uShallowColor', hexToLinear(look.color, [0.012, 0.15, 0.25]));
            m.setParameter('uDeepColor', hexToLinear(look.deepColor, [0.001, 0.02, 0.04]));
            m.setParameter('uWave', [strength, freq, dir, still ? 1 : 0]);
            this.setSwell(m, look, strength, dir);
            m.setParameter('uLook', [look.reflectivity, look.refraction, look.caustics, look.foam]);
            const sun = this.sceneSun;
            // The fallback direction is in scene (editor) coordinates: z is mirrored in PlayCanvas.
            const fallback = sunDirection(look.sunAzimuth, look.sunElevation);
            const sunDir = sun ? [sun.dir.x, sun.dir.y, sun.dir.z] : [fallback[0], fallback[1], -fallback[2]];
            const sunColor = sun ? sun.color : [1.0, 0.95, 0.86];
            m.setParameter('uSunDir', sunDir);
            m.setParameter('uSunColor', sunColor);
            m.setParameter('uLook2', [look.sunGlint, look.stylized ? 1 : 0, Math.min(1, Math.max(0, sunDir[1] * 6)), 0]);
            m.setParameter('uSkyZenith', zenith);
            m.setParameter('uSkyHorizon', horizon);
            m.setParameter('uWaterDetail', texture);
            m.setParameter('uPixel', pixel);
            m.setParameter('uWaterMeshDepth', [this.depthMapRequested ? 1 : 0, 0, 0, 0]);
        }
        this.updateReflections(camPos);
    }

    private setSwell(m: pc.Material, look: WaterLook, strength: number, dir: number): void {
        // Four steep-sine swell waves: wavelengths (scene units), angle offsets and steepness.
        const lengths = [9.0, 5.5, 3.3, 2.0];
        const angles = [0, 0.5, -0.7, 1.2];
        const steep = [0.32, 0.26, 0.2, 0.15];
        const quantum = (Math.PI * 2) / WATER_TIME_PERIOD;
        for (let i = 0; i < 4; i++) {
            const k = (Math.PI * 2) / (lengths[i] / Math.max(look.waveScale, 0.05));
            const a = dir + angles[i];
            // deep-water dispersion, rounded so the phase repeats every WATER_TIME_PERIOD
            const omega = Math.max(1, Math.round(Math.sqrt(9.8 * k) / quantum)) * quantum;
            m.setParameter(`uSwell${i}`, [Math.cos(a) * k, Math.sin(a) * k, omega, steep[i] * strength * 0.5]);
        }
    }

    private isOnScreen(plane: WaterPlaneRuntime, cam: pc.CameraComponent | undefined): boolean {
        // `enabled` is false when the entity or any parent is off (outliner eye, keyframed visibility).
        if (!plane.entity.enabled) return false;
        const mi = plane.meshChild.render?.meshInstances?.[0];
        if (!mi || !cam) return false;
        return aabbInFrustum(mi.aabb, cam.camera.frustum, 0.25);
    }

    /** The scene's main sun: the brightest enabled directional light shining down. */
    private scanSun(): void {
        const lights = this.deps.app.root.findComponents('light') as pc.LightComponent[];
        let best: pc.LightComponent | null = null;
        for (const light of lights) {
            if (!light.enabled || !light.entity.enabled || light.type !== 'directional') continue;
            if (light.entity.up.y < 0.05) continue;
            if (!best || light.intensity > best.intensity) best = light;
        }
        if (!best) {
            this.sceneSun = null;
            return;
        }
        const c = best.color;
        const k = Math.min(Math.max(best.intensity, 0.2), 3);
        this.sceneSun = {
            dir: best.entity.up.clone().normalize(),
            color: [srgbToLinear(c.r) * k, srgbToLinear(c.g) * k, srgbToLinear(c.b) * k],
        };
    }

    // ── frame copy (refraction) ──────────────────────────────────────────────────────────

    private setGrab(on: boolean): void {
        const cam = this.deps.camera.camera;
        if (on && !this.depthLayerMoved) this.moveDepthLayer();
        if (cam && on !== this.grabRequested) {
            cam.requestSceneColorMap(on);
            this.grabRequested = on;
            // The grab pass binds its copy every frame it runs; while it doesn't, a placeholder
            // keeps a plane that just came into view from sampling a released texture.
            if (!on) this.deps.app.graphicsDevice.scope.resolve('uSceneColorMap').setValue(this.placeholderTexture());
        }
        const frame = this.deps.cameraFrame();
        if (this.cameraFrameGrab && this.cameraFrameGrab !== frame) this.cameraFrameGrab = null;
        if (frame) {
            const options = (frame as unknown as { options?: { lastGrabLayerId?: number; lastGrabLayerIsTransparent?: boolean } }).options;
            if (on && options) {
                options.lastGrabLayerId = pc.LAYERID_WORLD;
                options.lastGrabLayerIsTransparent = true;
            }
            if (frame.rendering.sceneColorMap !== on && (on || this.cameraFrameGrab === frame)) {
                frame.rendering.sceneColorMap = on;
                frame.update();
                this.cameraFrameGrab = on ? frame : null;
            }
        }
    }

    /**
     * The main camera's scene depth map (the engine's depth grab at the Depth layer, or the
     * CameraFrame's prepass with post-processing), for models standing in the water. While it is
     * not taken a placeholder is bound, so a plane never samples a released texture.
     */
    private setDepthMap(on: boolean): void {
        const cam = this.deps.camera.camera;
        if (on && !this.depthLayerMoved) this.moveDepthLayer();
        if (cam && on !== this.depthMapRequested) {
            cam.requestSceneDepthMap(on);
            this.depthMapRequested = on;
            this.deps.requestFrame('water-model-depth');
        }
        if (!this.depthMapRequested) this.deps.app.graphicsDevice.scope.resolve('uSceneDepthMap').setValue(this.placeholderTexture());
        const frame = this.deps.cameraFrame();
        if (this.cameraFrameDepth && this.cameraFrameDepth !== frame) this.cameraFrameDepth = null;
        if (frame && frame.rendering.sceneDepthMap !== on && (on || this.cameraFrameDepth === frame)) {
            frame.rendering.sceneDepthMap = on;
            frame.update();
            this.cameraFrameDepth = on ? frame : null;
        }
    }

    /** The engine grabs the scene colour and depth at the Depth layer: put it right after the splats. */
    private moveDepthLayer(): void {
        this.depthLayerMoved = true;
        const layers = this.deps.app.scene.layers;
        const depth = layers.getLayerById(pc.LAYERID_DEPTH);
        const world = layers.getLayerById(pc.LAYERID_WORLD);
        if (!depth || !world) return;
        layers.removeOpaque(depth);
        const worldT = layers.getTransparentIndex(world);
        layers.insertOpaque(depth, worldT >= 0 ? worldT + 1 : layers.layerList.length);
    }

    // ── planar reflection ────────────────────────────────────────────────────────────────

    private readonly reflections = new Map<string, WaterReflection>();
    private readonly reflMat = new pc.Mat4();
    private readonly reflTarget = new pc.Vec3();
    private readonly reflUp = new pc.Vec3();
    private readonly reflClear = new pc.Color(0, 0, 0, 0);

    private bindReflection(plane: WaterPlaneRuntime, texture: pc.Texture | null): void {
        const t = texture ?? this.placeholderTexture();
        if (plane.boundReflection === t) return;
        plane.boundReflection = t;
        plane.material.setParameter('uReflectionMap', t);
    }

    private releaseReflection(r: WaterReflection): void {
        this.reflections.delete(r.key);
        for (const plane of this.planes.values()) if (plane.boundReflection === r.texture) this.bindReflection(plane, null);
        // As the mirrors: leave the composition now, destroy once a frame rendered without it.
        r.entity.enabled = false;
        const { app } = this.deps;
        app.once('postrender', () => {
            r.entity.destroy();
            r.target.destroy();
            r.texture.destroy();
        });
        app.renderNextFrame = true;
    }

    private reflectionSize(): { width: number; height: number } {
        const device = this.deps.app.graphicsDevice;
        const scale = Math.min(0.5, 1280 / Math.max(device.width, 1));
        return { width: Math.max(64, Math.floor(device.width * scale)), height: Math.max(64, Math.floor(device.height * scale)) };
    }

    private createReflection(key: string, normal: pc.Vec3, d: number): WaterReflection {
        const { app } = this.deps;
        const device = app.graphicsDevice;
        const { width, height } = this.reflectionSize();
        const texture = new pc.Texture(device, {
            name: `water-reflection-${this.reflections.size}`,
            width,
            height,
            format: pc.PIXELFORMAT_RGBA8,
            mipmaps: false,
            minFilter: pc.FILTER_LINEAR,
            magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const target = new pc.RenderTarget({ colorBuffer: texture, depth: true, origin: pc.RENDERTARGET_ORIGIN_BOTTOM });
        const entity = new pc.Entity(`water-reflection-camera`);
        entity.addComponent('camera', {
            priority: -90 - this.reflections.size,
            renderTarget: target,
            layers: [pc.LAYERID_WORLD, pc.LAYERID_SKYBOX],
            flipFaces: true,
            clearColor: new pc.Color(0, 0, 0, 0),
        });
        // Raw colours: the water shader decodes and tone maps them once.
        entity.camera!.toneMapping = pc.TONEMAP_NONE;
        entity.camera!.gammaCorrection = pc.GAMMA_SRGB;
        app.root.addChild(entity);
        const r: WaterReflection = {
            key, entity, target, texture,
            normal: normal.clone(), d,
            clip: new Float32Array([normal.x, normal.y, normal.z, d]),
            reflPos: new pc.Vec3(),
            lastTarget: new pc.Vec3(Infinity, 0, 0),
            lastUp: new pc.Vec3(),
            lastPos: new pc.Vec3(Infinity, 0, 0),
            usedThisFrame: false, idleFrames: 0, outOfViewFrames: 0,
        };
        // The splat clip plane for the GPU-sort projection (patchGpuSortClip) and the draw (below).
        setSplatClipPlane(entity, r.clip);
        const reflCam = entity.camera!;
        (reflCam as unknown as { calculateProjection: ProjectionCallback }).calculateProjection = (projMatrix: pc.Mat4) => {
            const aspect = device.width / device.height;
            projMatrix.setPerspective(reflCam.fov, aspect, reflCam.nearClip, reflCam.farClip);
            // Splats below the water are clipped in the reflection (shared mirror clip uniform).
            device.scope.resolve('uMirrorClipPlane').setValue(r.clip);
            // Oblique near plane (Lengyel) for meshes.
            const { x: nx, y: ny, z: nz } = r.normal;
            const vm = reflCam.viewMatrix.data;
            const cx = vm[0] * nx + vm[4] * ny + vm[8] * nz;
            const cy = vm[1] * nx + vm[5] * ny + vm[9] * nz;
            const cz = vm[2] * nx + vm[6] * ny + vm[10] * nz;
            const cw = r.reflPos.x * nx + r.reflPos.y * ny + r.reflPos.z * nz - r.d;
            if (cz >= 0) return;
            const pm = projMatrix.data;
            const qx = (Math.sign(cx) + pm[8]) / pm[0];
            const qy = (Math.sign(cy) + pm[9]) / pm[5];
            const qz = -1.0;
            const qw = (1.0 + pm[10]) / pm[14];
            const dot = cx * qx + cy * qy + cz * qz + cw * qw;
            if (Math.abs(dot) < 1e-6) return;
            const scale = 1.0 / dot;
            pm[2] = cx * scale;
            pm[6] = cy * scale;
            pm[10] = cz * scale;
            pm[14] = cw * scale;
        };
        this.reflections.set(key, r);
        return r;
    }

    /**
     * Assign every visible planar plane to the reflection of its level (creating one while under
     * the cap; beyond it the plane reflects the sky), then update the cameras in use and retire
     * idle ones.
     */
    private updateReflections(camPos: pc.Vec3): void {
        for (const r of this.reflections.values()) r.usedThisFrame = false;
        for (const plane of this.planes.values()) {
            if (!plane.planar) continue;
            const normal = this.n.copy(plane.entity.up).normalize();
            const d = normal.dot(plane.entity.getPosition());
            // Seen from below the water there is no reflection to show.
            if (!plane.visible || normal.dot(camPos) - d < -0.05) continue;
            // Same level = same plane: normals within ~0.6 degrees, heights within ~1 cm.
            const key = `${Math.round(normal.x * 100)},${Math.round(normal.y * 100)},${Math.round(normal.z * 100)},${Math.round(d * 100)}`;
            let r = this.reflections.get(key);
            if (!r && this.reflections.size < MAX_REFLECTION_PLANES) r = this.createReflection(key, normal, d);
            if (!r) {
                this.bindReflection(plane, null);
                continue;
            }
            r.usedThisFrame = true;
            this.bindReflection(plane, r.texture);
        }
        const main = this.deps.camera;
        const mainCam = main.camera;
        for (const r of [...this.reflections.values()]) {
            if (r.usedThisFrame) {
                r.outOfViewFrames = 0;
                r.idleFrames = 0;
                if (!r.entity.enabled) r.entity.enabled = true;
            } else {
                if (r.entity.enabled && ++r.outOfViewFrames >= REFLECTION_DISABLE_AFTER_FRAMES) r.entity.enabled = false;
                if (++r.idleFrames >= REFLECTION_RELEASE_AFTER_FRAMES) {
                    this.releaseReflection(r);
                    continue;
                }
            }
            if (!r.entity.enabled || !mainCam) continue;
            this.deps.ensureSplatClip();
            if (!this.gpuSortClipPatched) this.gpuSortClipPatched = patchGpuSortClip(this.deps.app);
            // Follow canvas resizes.
            const { width, height } = this.reflectionSize();
            if (r.texture.width !== width || r.texture.height !== height) r.target.resize(width, height);
            const reflCam = r.entity.camera!;
            reflCam.fov = mainCam.fov;
            reflCam.nearClip = mainCam.nearClip;
            reflCam.farClip = mainCam.farClip;
            const clear = mainCam.clearColor;
            if (this.reflClear.r !== clear.r || this.reflClear.g !== clear.g || this.reflClear.b !== clear.b) {
                this.reflClear.set(clear.r, clear.g, clear.b, 0);
                reflCam.clearColor = this.reflClear;
            }
            this.reflMat.setReflection(r.normal, -r.d);
            this.reflMat.transformPoint(camPos, r.reflPos);
            this.reflTarget.copy(camPos).add(main.forward);
            this.reflMat.transformPoint(this.reflTarget, this.reflTarget);
            this.reflUp.copy(main.up);
            this.reflMat.transformVector(this.reflUp, this.reflUp);
            // Write the transform only when it changes: a node moved every frame keeps on-demand
            // rendering from ever idling (OnDemandRenderScheduler's transform probe).
            if (!r.lastPos.equalsApprox(r.reflPos, 1e-6) || !r.lastTarget.equalsApprox(this.reflTarget, 1e-6) || !r.lastUp.equalsApprox(this.reflUp, 1e-6)) {
                r.entity.setPosition(r.reflPos);
                r.entity.lookAt(this.reflTarget, this.reflUp);
                r.lastPos.copy(r.reflPos);
                r.lastTarget.copy(this.reflTarget);
                r.lastUp.copy(this.reflUp);
            }
        }
    }
}
